import React from "react";
import _ from "lodash";
import { Link } from "react-router-dom";
import { FormattedMessage } from "react-intl";

import { DataRow } from "@ui/DataRow";
import { AlbumPack } from "../AlbumPack";
import { AlbumsList } from "../AlbumsList";
import { ArtistsList } from "../ArtistsList";
import { Copyright } from "../Copyright";
import { Track as Model, Album, Artist } from "../../models";
import { messages } from "../../messages";
import { WaIcon } from "@app/modules/ui/WaIcon";

export interface TrackProps {
    track: Model;
    album: Album;
    style?: "headline";
    small?: boolean;
    link?: boolean;
    children?: React.ReactNode;
    additionalDetails?: boolean;
}

export const Track = ({ track, album, small, link, children, style, additionalDetails }: TrackProps) => {
    // Build the info.
    const info: JSX.Element[] = [];
    let className = "track";
    let TitleTag = "span";
    let titleClass = "track__title";
    // const showAlbum = style === "headline";
    const showAlbum = false;
    link = link == null || link; // default link to true
    if (style === "headline") {
        className += " track--headline";
        TitleTag = "h1";
        titleClass += " display-1";
    }
    else {
        className += " track--list-item";
    }
    let albums: Album[] = [];
    let trackUrl = null;
    if (track != null) {
        albums = _.reduce(track.albums,
            (result: Album[], a: Album | number) => {
                if (typeof a !== "number") {
                    result.push(a);
                }
                return result;
            }, [],
        );
        if (album != null) {
            albums = [album];
        }

        if (style === "headline") {
            info.push(
                <h5 key="format" className="track__format">
                    <WaIcon name="note" />
                    {" "}
                    <FormattedMessage {...messages.trackEntitySingular} />
                </h5>,
            );
        }
        // Title
        const titleChildren: React.ReactNode[] = [track.getTitle()];
        if (track.titleVersion != null && track.titleVersion !== "") {
            titleChildren.push(
                <>&nbsp;</>,
                <small key="version" className="track__title__version">
                    ({track.titleVersion})
                </small>,
            );
        }
        let trackEl = React.createElement(
            TitleTag,
            { key: "title", className: titleClass },
            ...titleChildren,
        );
        trackUrl = track.getViewUrl();
        if (trackUrl != null && link) {
            trackEl = <Link key="title" to={trackUrl} className="track__link">{trackEl}</Link>;
        }
        info.push(trackEl);
        // Artists
        if (track.artists != null && track.artists.length > 0) {
            info.push(
                <span key="artists" className="track__artists">
                    <ArtistsList showImage artists={track.artists as Artist[]} />
                </span>,
            );
        }
        // Albums
        if (showAlbum && albums.length > 0) {
            info.push(
                <span key="album" className="track__album">
                    <AlbumsList albums={albums} />
                </span>,
            );
        }
        if (additionalDetails) {
            const details: JSX.Element[] = [];
            if (track.isrc) {
                details.push(
                    <DataRow
                        key="isrc"
                        message={messages.trackColumnIsrc}
                        value={track.isrc}
                    />,
                );
            }
            if (track.duration) {
                details.push(
                    <DataRow
                        key="duration"
                        message={messages.trackColumnDuration}
                        value={track.durationFormatted}
                    />,
                );
            }
            info.push(
                <div key="details" className="track__details">
                    <h4 className="display-6">
                        <FormattedMessage {...messages.trackInformation} />
                    </h4>
                    <div className="track__details__grid">
                        {details}
                    </div>
                    <Copyright key="copyright" entity={track} />
                </div>,
            );
        }
    }
    return (
        <div className={className}>
            <div className="track__artwork">
                <AlbumPack
                    album={albums.length === 0 ? null : albums[0]}
                    size={small ? "small" : undefined}
                    link={link && trackUrl != null ? track.getViewUrl() : false}
                />
            </div>
            <div className="track__info">{info}</div>
            <div className="track__additions">{children}</div>
        </div>
    );
};
